import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SyncBar                  from "./SyncBar";
import StatChips                from "./StatChips";
import FilterTabs               from "./FilterTabs";
import AssociateTable           from "./AssociateTable";
import AllocationHistoryDrawer  from "./AllocationHistoryDrawer";
import useAuth                  from "../../../../../hooks/useAuth";
import { ROLES }                from "../../../../../constants/auth";
import { getAssociates, getAssociatesStats, addAssociateToBench } from "../../../../../api/associates";
import PageInfo                 from "../../../../../components/common/PageInfo";
import { exportAssociates }     from "./exportAssociates";

// Derive the current quarter string e.g. "Q2-2026" from today's date
const getCurrentQuarter = () => {
  const now = new Date();
  const q   = Math.ceil((now.getMonth() + 1) / 3);
  return `Q${q}-${now.getFullYear()}`;
};

const AssociateMaster = ({ initialFilter = "billable" }) => {
  const theme = useTheme();
  const t     = theme.custom;
  const { user } = useAuth();

  const isSCG = user?.role === ROLES.SCG;

  const [stats,        setStats]        = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [search,       setSearch]       = useState("");

  const [historyAssociate, setHistoryAssociate] = useState(null);
 


  useEffect(() => {
    setStatsLoading(true);
    getAssociatesStats()
      .then(setStats)
      .catch((err) => console.error("Failed to load associate stats:", err))
      .finally(() => setStatsLoading(false));
  }, []);

  

  const fetchAssociates = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const params = {};
      if (activeFilter !== "all") params.filter = activeFilter;
      const data = await getAssociates(params);
      setRows(data);
    } catch (err) {
      console.error("Failed to load associates:", err);
      setError("Failed to load associates");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => { fetchAssociates(); }, [fetchAssociates]);

  // Called by AssociateTable when SCG clicks "+ Bench" on a row
  const handleAddToBench = useCallback(async (associateId) => {
    const quarter = getCurrentQuarter();
    await addAssociateToBench(associateId, quarter);
    // Silent refresh — no loading spinner, no scroll reset
    fetchAssociates(true);
    getAssociatesStats().then(setStats).catch(() => {});
  }, [fetchAssociates]);

  const summaryForComponents = stats ? {
  totalAssociates:      stats.total,
  billable:             stats.billable,          // ← NEW
  fullyAllocated:       stats.fullyAllocated,
  partiallyAllocated:   stats.partial,
  available:            stats.available,
  newPendingAllocation: stats.newCount,
  nonBillable:          stats.nonBillable,       // ← from stats directly now
  lastSyncAt:           stats.lastSyncedAt,
  syncStatus:           stats.lastSyncedAt ? "synced" : null,
  billedAssociates:     stats?.billedAssociates,
  unBilledAssociates:   stats?.unBilledAssociates,
} : null;

  const filteredRows = rows.filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      row.fullName?.toLowerCase().includes(q)  ||
      row.levelCode?.toLowerCase().includes(q) ||
      row.levelName?.toLowerCase().includes(q) ||
      row.skills?.some((s) => s.toLowerCase().includes(q))
    );
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>

      {/* Page title */}
      <Box sx={{ padding: "20px 20px 0" }}>
        <Typography sx={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "22px", fontWeight: 700,
          color: t.text, letterSpacing: "-0.01em",
        }}>
          Associate Master
        </Typography>
      </Box>

      <SyncBar summary={summaryForComponents} />
      <StatChips summary={summaryForComponents} loading={statsLoading} />
      <PageInfo message="Synced nightly from Zoho People, includes Contractors"/>
      <FilterTabs
        activeFilter={activeFilter}
        onFilterChange={(f) => { setActiveFilter(f); setSearch(""); }}
        search={search}
        onSearchChange={setSearch}
        summary={summaryForComponents}
        onExport={() => exportAssociates(filteredRows, activeFilter)}
        exportDisabled={loading || filteredRows.length === 0}
      />

      {error && (
        <Box sx={{
          padding: "10px 20px", flexShrink: 0,
          fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#c84040",
          borderBottom: `1px solid ${t.border}`,
        }}>
          {error}
        </Box>
      )}

      <AssociateTable
        rows={filteredRows}
        loading={loading}
        isSCG={isSCG}
        onAddToBench={handleAddToBench}
        onViewHistory={setHistoryAssociate}
      />

      <AllocationHistoryDrawer
        associate={historyAssociate}
        onClose={() => setHistoryAssociate(null)}
      />
    </Box>
  );
};

export default AssociateMaster;